import { useState } from 'react';
import { useLocation } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { AdvancedAIChat } from '@/components/AdvancedAIChat';
import { useAIStats } from '@/hooks/useAIStats';
import { useCaseDetails } from '@/hooks/useCaseDetails';
import { 
  Sparkles, 
  MessageSquare,
} from 'lucide-react';
import { type AITaskType } from '@/hooks/useAI';
import { assistants, quickActions } from '@/config/aiConfig';

const AICenter = () => {
  const location = useLocation();
  const caseIdFromLocation = (location.state as { caseId?: string })?.caseId;

  const [selectedAssistant, setSelectedAssistant] = useState<AITaskType>(() =>
    caseIdFromLocation ? 'case_analysis' : 'general'
  );
  const [activeTab, setActiveTab] = useState<string>(() =>
    caseIdFromLocation ? 'assistants' : 'assistants'
  );
  const [currentInitialPrompt, setCurrentInitialPrompt] = useState<string | undefined>(undefined);

  const { stats, loading: loadingStats, error: errorStats } = useAIStats();
  const { caseDetails, suggestedPrompts, loading: loadingCaseDetails, error: caseDetailsError } = useCaseDetails(caseIdFromLocation);

  const selectedAssistantData = assistants.find(a => a.id === selectedAssistant);

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="text-center space-y-2">
        <h1 className="text-3xl font-bold flex items-center justify-center gap-2">
          <Sparkles className="w-8 h-8 text-primary" />
          Centro de IA Jurídica
        </h1>
        <p className="text-lg text-gray-600 dark:text-gray-400">
          Assistentes especializados para otimizar seu trabalho jurídico
        </p>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="grid w-full grid-cols-3">
          <TabsTrigger value="assistants">Assistentes</TabsTrigger>
          <TabsTrigger value="chat">Chat Avançado</TabsTrigger>
          <TabsTrigger value="quick">Ações Rápidas</TabsTrigger>
        </TabsList>

        <TabsContent value="assistants" className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {assistants.map((assistant) => {
              const Icon = assistant.icon;
              return (
                <Card 
                  key={assistant.id}
                  className={`cursor-pointer transition-all hover:shadow-lg border-2 ${
                    selectedAssistant === assistant.id 
                      ? assistant.color 
                      : 'border-border hover:border-primary/20'
                  }`}
                  onClick={() => setSelectedAssistant(assistant.id)}
                >
                  <CardHeader className="pb-3">
                    <div className="flex items-start gap-3">
                      <div className={`w-12 h-12 rounded-lg flex items-center justify-center ${assistant.color}`}>
                        <Icon className="w-6 h-6" />
                      </div>
                      <div className="flex-1">
                        <CardTitle className="text-lg">{assistant.title}</CardTitle>
                        <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                          {assistant.description}
                        </p>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-2">
                      <p className="text-sm font-medium">Exemplos de uso:</p>
                      <ul className="text-xs text-muted-foreground space-y-1">
                        {assistant.examples.map((example, index) => (
                          <li key={index}>• {example}</li>
                        ))}
                      </ul>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>

          {selectedAssistantData && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <selectedAssistantData.icon className="w-5 h-5" />
                  {selectedAssistantData.title}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <AdvancedAIChat
                  initialTaskType={selectedAssistant}
                  title={`Assistente: ${selectedAssistantData.title}`}
                  placeholder={`Faça uma pergunta sobre ${selectedAssistantData.title.toLowerCase()}...`}
                  caseId={caseIdFromLocation}
                  initialPrompt={currentInitialPrompt} // Pass the initial prompt
                />
              </CardContent>
            </Card>
          )}

          {loadingCaseDetails ? (
            <div className="text-center text-muted-foreground">Carregando sugestões de processo...</div>
          ) : caseDetailsError ? (
            <div className="text-center text-destructive">Erro ao carregar sugestões de processo: {caseDetailsError}</div>
          ) : caseIdFromLocation && suggestedPrompts.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Sparkles className="w-5 h-5 text-purple-500" />
                  Sugestões Inteligentes para o Processo
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {suggestedPrompts.map((prompt, index) => (
                    <Button 
                      key={index} 
                      variant="outline" 
                      className="justify-start text-left h-auto p-3"
                      onClick={() => {
                        setSelectedAssistant('case_analysis');
                        setCurrentInitialPrompt(prompt);
                        setActiveTab('assistants');
                      }}
                    >
                      {prompt}
                    </Button>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        <TabsContent value="chat">
          <Card>
            <CardHeader>
              <CardTitle className="text-xl font-semibold flex items-center gap-2">
                <MessageSquare className="w-5 h-5" />
                Chat IA Avançado
              </CardTitle>
            </CardHeader>
            <CardContent>
              <AdvancedAIChat fullScreen={false} />
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="quick" className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {quickActions.map((action, index) => {
              const Icon = action.icon;
              return (
                <Card key={index} className="cursor-pointer hover:shadow-lg transition-all">
                  <CardContent className="p-6">
                    <div className="flex items-center gap-4">
                      <div className={`w-12 h-12 rounded-lg flex items-center justify-center ${action.color}`}>
                        <Icon className="w-6 h-6" />
                      </div>
                      <div className="flex-1">
                        <h3 className="font-semibold text-lg">{action.title}</h3>
                        <p className="text-sm text-muted-foreground">
                          {action.description}
                        </p>
                      </div>
                      <Button onClick={() => action.action(setSelectedAssistant, setActiveTab)}>
                        Iniciar
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>

          <Card>
            <CardHeader>
              <CardTitle className="text-xl font-semibold">Estatísticas de Uso da IA</CardTitle>
            </CardHeader>
            <CardContent>
              {loadingStats ? (
                <div className="text-center text-muted-foreground">Carregando estatísticas...</div>
              ) : errorStats ? (
                <div className="text-center text-destructive">{errorStats}</div>
              ) : (
                <>
                  <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                    <div className="text-center">
                      <div className="text-2xl font-bold text-primary">{stats?.totalInteractions || 0}</div>
                      <div className="text-sm text-muted-foreground">Total de Interações</div>
                    </div>
                    <div className="text-center">
                      <div className="text-2xl font-bold text-green-500">{stats?.successfulInteractions || 0}</div>
                      <div className="text-sm text-muted-foreground">Interações Bem-sucedidas</div>
                    </div>
                    <div className="text-center">
                      <div className="text-2xl font-bold text-red-500">{stats?.failedInteractions || 0}</div>
                      <div className="text-sm text-muted-foreground">Interações Falhas</div>
                    </div>
                    <div className="text-center">
                      <div className="text-3xl font-extrabold text-blue-500">{stats?.averageResponseLength.toFixed(0) || 0}</div>
                      <div className="text-sm text-gray-500 dark:text-gray-400">Média de Caracteres/Resposta</div>
                    </div>
                  </div>

                  <div className="mt-6">
                    <h4 className="text-lg font-semibold mb-3">Interações por Tipo de Assistente</h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                      {Object.entries(stats?.interactionsByType || {}).map(([type, count]) => (
                        <Card key={type} className="p-3">
                          <p className="text-sm font-medium">{assistants.find(a => a.id === type)?.title || type}</p>
                          <p className="text-2xl font-extrabold text-gray-800 dark:text-gray-200">{count}</p>
                        </Card>
                      ))}
                    </div>
                  </div>

                  <div className="mt-6">
                    <h4 className="text-xl font-bold mb-3 text-gray-800 dark:text-gray-200">Taxa de Satisfação</h4>
                    <div className="text-center">
                      <div className="text-4xl font-bold text-purple-600">
                        {stats?.successfulInteractions && stats?.totalInteractions > 0
                          ? `${((stats.successfulInteractions / stats.totalInteractions) * 100).toFixed(0)}%`
                          : 'N/A'}
                      </div>
                      <p className="text-base text-gray-500 dark:text-gray-400">Baseado em interações bem-sucedidas</p>
                    </div>
                  </div>
                </>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default AICenter;